import { api, endpoints } from "api/api";
import { BaseApiPaging } from "api/types";
import { AxiosPromise } from "axios";

export enum KSeFEnvironment {
  Test = 1,
  Production = 2,
}

export enum KSeFSubmissionStatus {
  Pending = 1,
  Submitted = 2,
  Accepted = 3,
  Rejected = 4,
  Error = 5,
  RetryScheduled = 6,
}

export enum KSeFLogType {
  SubmissionQueued = 1,
  SubmissionSent = 2,
  SubmissionAccepted = 3,
  SubmissionRejected = 4,
  SubmissionError = 5,
  UpoRetrieved = 6,
  UpoRetrievalFailed = 7,
  RetryScheduled = 8,
  RetryAttempt = 9,
  SessionStarted = 10,
  SessionEnded = 11,
  ApiConnectionTested = 12,
  InfoMessage = 13,
  WarningMessage = 14,
  ErrorMessage = 15,
  XmlGenerationFailed = 16,
}

export interface KSeFIntegrationApi {
  id: number;
  name: string;
  environment: KSeFEnvironment;
  baseUrl: string;
  nip: string;
  authType: string;
  isEnabled: boolean;
  autoSubmit: boolean;
  sellerName?: string;
  sellerAddress?: string;
  sellerNIP?: string;
  sellerBankAccount?: string;
  hasCertificate: boolean;
  createdDate: Date;
  lastModified: Date;
}

export interface KSeFIntegrationRequest {
  name: string;
  environment: KSeFEnvironment;
  baseUrl: string;
  nip: string;
  authToken?: string;
  authType: string;
  isEnabled: boolean;
  autoSubmit: boolean;
  sellerName?: string;
  sellerAddress?: string;
  sellerNIP?: string;
  sellerBankAccount?: string;
}

export interface KSeFSubmissionDto {
  id: number;
  invoiceId: number;
  ksefNumber?: string;
  status: KSeFSubmissionStatus;
  submittedAt?: Date;
  acceptedAt?: Date;
  errorMessage?: string;
  retryCount: number;
  createdDate: Date;
}

export interface KSeFSubmissionDetailDto {
  id: number;
  invoiceId: number;
  ksefIntegrationId: number;
  ksefNumber?: string;
  ksefReferenceNumber?: string;
  fa3Xml?: string;
  status: KSeFSubmissionStatus;
  submittedAt?: Date;
  acceptedAt?: Date;
  upoXml?: string;
  upoRetrievedAt?: Date;
  errorMessage?: string;
  errorCode?: string;
  retryCount: number;
  maxRetries: number;
  nextRetryAt?: Date;
  syncSessionId?: string;
  paymentId?: number;
  registrationId?: number;
  createdDate: Date;
}

export interface KSeFLogDto {
  id: number;
  type: KSeFLogType;
  actionTitle: string;
  details?: string;
  errorDetails?: string;
  syncSessionId?: string;
  invoiceId?: number;
  ksefInvoiceSubmissionId?: number;
  createdDate: Date;
}

export interface KSeFDashboardDto {
  pending: number;
  submitted: number;
  accepted: number;
  rejected: number;
  errors: number;
  retryScheduled: number;
}

export interface KSeFTestConnectionResult {
  success: boolean;
}

const fetch = (): AxiosPromise<KSeFIntegrationApi[]> => {
  return api.get<KSeFIntegrationApi[]>(endpoints.KSEF.GET());
};

const fetchById = (id: number): AxiosPromise<KSeFIntegrationApi> => {
  return api.get<KSeFIntegrationApi>(endpoints.KSEF.GET_BY_ID(id));
};

const create = (body: KSeFIntegrationRequest): AxiosPromise<KSeFIntegrationApi> => {
  return api.post<KSeFIntegrationApi>(endpoints.KSEF.CREATE(), body);
};

const update = (id: number, body: KSeFIntegrationRequest): AxiosPromise<KSeFIntegrationApi> => {
  return api.put<KSeFIntegrationApi>(endpoints.KSEF.UPDATE(id), body);
};

const remove = (id: number): Promise<void> => {
  return api.delete(endpoints.KSEF.DELETE(id));
};

const testConnection = (id: number): AxiosPromise<KSeFTestConnectionResult> => {
  return api.post<KSeFTestConnectionResult>(endpoints.KSEF.TEST_CONNECTION(id));
};

const fetchDashboard = (): AxiosPromise<KSeFDashboardDto> => {
  return api.get<KSeFDashboardDto>(endpoints.KSEF.GET_DASHBOARD());
};

const fetchSubmissions = (
  id: number,
  skip: number = 0,
  take: number = 20,
  status?: KSeFSubmissionStatus,
): AxiosPromise<KSeFSubmissionDto[]> => {
  return api.get<KSeFSubmissionDto[]>(endpoints.KSEF.GET_SUBMISSIONS(id, skip, take, status));
};

const fetchSubmission = (id: number, submissionId: number): AxiosPromise<KSeFSubmissionDetailDto> => {
  return api.get<KSeFSubmissionDetailDto>(endpoints.KSEF.GET_SUBMISSION(id, submissionId));
};

const retrySubmission = (id: number, submissionId: number): AxiosPromise => {
  return api.post(endpoints.KSEF.RETRY_SUBMISSION(id, submissionId));
};

const downloadUpo = (id: number, submissionId: number): AxiosPromise<Blob> => {
  return api.get<Blob>(endpoints.KSEF.DOWNLOAD_UPO(id, submissionId), { responseType: "blob" });
};

const fetchLogs = (id: number, skip: number = 0, take: number = 50): AxiosPromise<BaseApiPaging<KSeFLogDto>> => {
  return api.get<BaseApiPaging<KSeFLogDto>>(endpoints.KSEF.GET_LOGS(id, skip, take));
};

const uploadCertificate = (id: number, file: File, password: string): AxiosPromise<KSeFIntegrationApi> => {
  const formData = new FormData();
  formData.append("certificate", file);
  formData.append("password", password);
  return api.post<KSeFIntegrationApi>(endpoints.KSEF.UPLOAD_CERTIFICATE(id), formData);
};

export const ksefApi = {
  fetch,
  fetchById,
  create,
  update,
  delete: remove,
  testConnection,
  fetchDashboard,
  fetchSubmissions,
  fetchSubmission,
  retrySubmission,
  downloadUpo,
  fetchLogs,
  uploadCertificate,
};
